import React, { useState, useEffect } from 'react';
import { 
  UserPlus, CheckCircle, Clock, AlertCircle, FileText, 
  Mail, Phone, MapPin, Calendar, Award, Building2, 
  ArrowRight, ArrowLeft, Save, Send, Eye, Edit
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { toast } from '../hooks/use-toast';
import { apiService } from '../services/apiService';

const getSchoolKey = (school) => String(school?.key || school?.school_number || school?.id || '');

const EmployeeOnboarding = ({ onClose, onComplete }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [onboardingData, setOnboardingData] = useState({
    // Basic Information
    first_name: '',
    last_name: '',
    middle_name: '',
    preferred_name: '',
    email: '',
    phone_primary: '',
    phone_secondary: '',
    
    // Address
    address_line1: '',
    address_line2: '',
    city: '',
    state: '',
    zip_code: '',
    
    // Employment
    position_title: '',
    department: '',
    school_id: '',
    hire_date: new Date().toISOString().split('T')[0],
    fte: 1.0,
    salary: '',
    supervisor_id: '',
    
    // Professional
    state_teacher_id: '',
    license_number: '',
    license_expiry: '',
    certifications: [],
    education_level: '',
    years_experience: 0,
    
    // Emergency Contacts
    emergency_contacts: [
      {
        name: '',
        relationship: '',
        phone_primary: '',
        phone_secondary: '',
        email: '',
        is_primary: true
      }
    ],
    
    // Documents
    required_documents: [],
    
    // Notes
    notes: ''
  });

  const [schools, setSchools] = useState([]);
  const [supervisors, setSupervisors] = useState([]);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const steps = [
    { id: 'basic', title: 'Basic Information', icon: UserPlus },
    { id: 'contact', title: 'Contact Details', icon: Phone },
    { id: 'employment', title: 'Employment Info', icon: Building2 },
    { id: 'professional', title: 'Professional Info', icon: Award },
    { id: 'emergency', title: 'Emergency Contacts', icon: AlertCircle },
    { id: 'documents', title: 'Documents', icon: FileText },
    { id: 'review', title: 'Review & Submit', icon: CheckCircle }
  ];

  useEffect(() => {
    loadSchools();
    loadSupervisors();
  }, []);

  const loadSchools = async () => {
    try {
      const schoolList = await apiService.getSchools();
      setSchools(schoolList);
    } catch (error) {
      console.error('Failed to load schools:', error);
    }
  };

  const loadSupervisors = async () => {
    try {
      const response = await apiService.makeRequest('/api/employees/search', {
        method: 'POST',
        body: JSON.stringify({
          department: 'Administration',
          limit: 100
        })
      });
      setSupervisors(response.employees || response);
    } catch (error) {
      console.error('Failed to load supervisors:', error);
    }
  };


  const validateStep = (step) => {
    // Basic validation for required fields based on step
    switch (step) {
      case 0: // Basic Info
        return onboardingData.first_name && onboardingData.last_name;
      case 1: // Contact Info
        return onboardingData.email;
      case 2: // Employment Details
        return onboardingData.position_title && onboardingData.department;
      case 3: // Review
        return true;
      default:
        return true;
    }
  };

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      if (validateStep(currentStep)) {
        setCurrentStep(currentStep + 1);
      } else {
        toast({
          title: 'Validation Error',
          description: 'Please fill in all required fields before continuing.',
          variant: 'destructive'
        });
      }
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSaveDraft = async () => {
    try {
      setSaving(true);
      // Save as draft - this would typically save to a drafts collection
      toast({
        title: 'Draft Saved',
        description: 'Onboarding progress has been saved as draft.'
      });
    } catch (error) {
      console.error('Failed to save draft:', error);
      toast({
        title: 'Error',
        description: 'Failed to save draft.',
        variant: 'destructive'
      });
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async () => {
    try {
      setSubmitting(true);
      
      // Create employee
      const employeeData = {
        first_name: onboardingData.first_name,
        last_name: onboardingData.last_name,
        middle_name: onboardingData.middle_name,
        preferred_name: onboardingData.preferred_name,
        email: onboardingData.email,
        phone_primary: onboardingData.phone_primary,
        phone_secondary: onboardingData.phone_secondary,
        address_line1: onboardingData.address_line1,
        address_line2: onboardingData.address_line2,
        city: onboardingData.city,
        state: onboardingData.state,
        zip_code: onboardingData.zip_code,
        position_title: onboardingData.position_title,
        department: onboardingData.department,
        school_id: onboardingData.school_id,
        hire_date: onboardingData.hire_date,
        fte: onboardingData.fte,
        salary: (() => {
          const parsedSalary = parseFloat(onboardingData.salary);
          return Number.isNaN(parsedSalary) ? null : parsedSalary;
        })(),
        supervisor_id: onboardingData.supervisor_id,
        state_teacher_id: onboardingData.state_teacher_id,
        license_number: onboardingData.license_number,
        license_expiry: onboardingData.license_expiry || null,
        education_level: onboardingData.education_level,
        years_experience: onboardingData.years_experience,
        notes: onboardingData.notes
      };

      const employee = await apiService.makeRequest('/api/employees/', {
        method: 'POST',
        body: JSON.stringify(employeeData)
      });

      // Add emergency contacts
      for (const contact of onboardingData.emergency_contacts) {
        if (contact.name && contact.phone_primary) {
          await apiService.makeRequest(`/api/employees/${employee.id}/emergency-contacts`, {
            method: 'POST',
            body: JSON.stringify(contact)
          });
        }
      }

      toast({
        title: 'Onboarding Complete',
        description: `${employee.first_name} ${employee.last_name} has been successfully onboarded.`
      });

      onComplete?.(employee);
      onClose();
    } catch (error) {
      console.error('Failed to complete onboarding:', error);
      toast({
        title: 'Onboarding Failed',
        description: 'Failed to complete employee onboarding.',
        variant: 'destructive'
      });
    } finally {
      setSubmitting(false);
    }
  };

  const updateData = (field, value) => {
    setOnboardingData(prev => ({ ...prev, [field]: value }));
  };

  const addEmergencyContact = () => {
    setOnboardingData(prev => ({
      ...prev,
      emergency_contacts: [
        ...prev.emergency_contacts,
        {
          name: '',
          relationship: '',
          phone_primary: '',
          phone_secondary: '',
          email: '',
          is_primary: false
        }
      ]
    }));
  };

  const updateEmergencyContact = (index, field, value) => {
    setOnboardingData(prev => ({
      ...prev,
      emergency_contacts: prev.emergency_contacts.map((contact, i) => 
        i === index ? { ...contact, [field]: value } : contact
      )
    }));
  };

  const removeEmergencyContact = (index) => {
    setOnboardingData(prev => ({
      ...prev,
      emergency_contacts: prev.emergency_contacts.filter((_, i) => i !== index)
    }));
  };

  const progress = ((currentStep + 1) / steps.length) * 100;

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center">
            <UserPlus className="w-5 h-5 mr-2 text-yellow-600" />
            Employee Onboarding Wizard
          </DialogTitle>
        </DialogHeader>

        {/* Progress Bar */}
        <div className="space-y-2">
          <div className="flex justify-between text-sm text-gray-600">
            <span>Step {currentStep + 1} of {steps.length}</span>
            <span>{Math.round(progress)}% Complete</span>
          </div>
          <Progress value={progress} className="w-full" />
        </div>

        {/* Step Navigation */}
        <div className="flex items-center justify-between py-4 border-b">
          <div className="flex items-center space-x-4">
            {steps.map((step, index) => {
              const Icon = step.icon;
              const isActive = index === currentStep;
              const isCompleted = index < currentStep;
              
              return (
                <div
                  key={step.id}
                  className={`flex items-center space-x-2 px-3 py-2 rounded-lg ${
                    isActive 
                      ? 'bg-yellow-100 text-yellow-800' 
                      : isCompleted 
                        ? 'bg-green-100 text-green-800'
                        : 'bg-gray-100 text-gray-600'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  <span className="text-sm font-medium hidden md:block">{step.title}</span>
                  {isCompleted && <CheckCircle className="w-4 h-4" />}
                </div>
              );
            })}
          </div>
        </div>

        {/* Step Content */}
        <div className="py-6">
          {currentStep === 0 && <BasicInformationStep data={onboardingData} updateData={updateData} />}
          {currentStep === 1 && <ContactDetailsStep data={onboardingData} updateData={updateData} />}
          {currentStep === 2 && <EmploymentInfoStep data={onboardingData} updateData={updateData} schools={schools} supervisors={supervisors} />}
          {currentStep === 3 && <ProfessionalInfoStep data={onboardingData} updateData={updateData} />}
          {currentStep === 4 && (
            <EmergencyContactsStep 
              data={onboardingData} 
              updateData={updateData}
              addContact={addEmergencyContact}
              updateContact={updateEmergencyContact}
              removeContact={removeEmergencyContact}
            />
          )}
          {currentStep === 5 && <DocumentsStep data={onboardingData} updateData={updateData} />}
          {currentStep === 6 && <ReviewStep data={onboardingData} schools={schools} supervisors={supervisors} />}
        </div>

        {/* Navigation Buttons */}
        <div className="flex justify-between items-center pt-6 border-t">
          <div className="flex space-x-2">
            <Button variant="outline" onClick={handleSaveDraft} disabled={saving}>
              {saving ? (
                <>
                  <Clock className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Save Draft
                </>
              )}
            </Button>
          </div>

          <div className="flex space-x-2">
            <Button
              variant="outline"
              onClick={handlePrevious}
              disabled={currentStep === 0}
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Previous
            </Button>

            {currentStep < steps.length - 1 ? (
              <Button
                onClick={handleNext}
                className="bg-yellow-500 hover:bg-yellow-600 text-black"
              >
                Next
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            ) : (
              <Button
                onClick={handleSubmit}
                disabled={submitting}
                className="bg-green-600 hover:bg-green-700 text-white"
              >
                {submitting ? (
                  <>
                    <Clock className="w-4 h-4 mr-2 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Complete Onboarding
                  </>
                )}
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Step Components
const BasicInformationStep = ({ data, updateData }) => (
  <div className="space-y-6">
    <div>
      <h3 className="text-lg font-semibold text-gray-900 mb-4">Basic Information</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label>First Name *</Label>
          <Input
            value={data.first_name}
            onChange={(e) => updateData('first_name', e.target.value)}
            required
          />
        </div>
        <div>
          <Label>Last Name *</Label>
          <Input
            value={data.last_name}
            onChange={(e) => updateData('last_name', e.target.value)}
            required
          />
        </div>
        <div>
          <Label>Middle Name</Label>
          <Input
            value={data.middle_name}
            onChange={(e) => updateData('middle_name', e.target.value)}
          />
        </div>
        <div>
          <Label>Preferred Name</Label>
          <Input
            value={data.preferred_name}
            onChange={(e) => updateData('preferred_name', e.target.value)}
            placeholder="If different from first name"
          />
        </div>
      </div>
    </div>
  </div>
);

const ContactDetailsStep = ({ data, updateData }) => (
  <div className="space-y-6">
    <div>
      <h3 className="text-lg font-semibold text-gray-900 mb-4">Contact Information</h3>
      <div className="space-y-4">
        <div>
          <Label>Email Address *</Label>
          <Input
            type="email"
            value={data.email}
            onChange={(e) => updateData('email', e.target.value)}
            required
          />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label>Primary Phone</Label>
            <Input
              value={data.phone_primary}
              onChange={(e) => updateData('phone_primary', e.target.value)}
            />
          </div>
          <div>
            <Label>Secondary Phone</Label>
            <Input
              value={data.phone_secondary}
              onChange={(e) => updateData('phone_secondary', e.target.value)}
            />
          </div>
        </div>
        <div>
          <Label>Address Line 1</Label>
          <Input
            value={data.address_line1}
            onChange={(e) => updateData('address_line1', e.target.value)}
          />
        </div>
        <div>
          <Label>Address Line 2</Label>
          <Input
            value={data.address_line2}
            onChange={(e) => updateData('address_line2', e.target.value)}
            placeholder="Apartment, suite, etc."
          />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <Label>City</Label>
            <Input
              value={data.city}
              onChange={(e) => updateData('city', e.target.value)}
            />
          </div>
          <div>
            <Label>State</Label>
            <Input
              value={data.state}
              onChange={(e) => updateData('state', e.target.value)}
            />
          </div>
          <div>
            <Label>ZIP Code</Label>
            <Input
              value={data.zip_code}
              onChange={(e) => updateData('zip_code', e.target.value)}
            />
          </div>
        </div>
      </div>
    </div>
  </div>
);

const EmploymentInfoStep = ({ data, updateData, schools, supervisors }) => (
  <div className="space-y-6">
    <div>
      <h3 className="text-lg font-semibold text-gray-900 mb-4">Employment Information</h3>
      <div className="space-y-4">
        <div>
          <Label>Position Title *</Label>
          <Input
            value={data.position_title}
            onChange={(e) => updateData('position_title', e.target.value)}
            required
          />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label>Department</Label>
            <Select 
              value={data.department} 
              onValueChange={(value) => updateData('department', value)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select department" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Teaching Staff">Teaching Staff</SelectItem>
                <SelectItem value="Administration">Administration</SelectItem>
                <SelectItem value="Support Staff">Support Staff</SelectItem>
                <SelectItem value="Maintenance">Maintenance</SelectItem>
                <SelectItem value="Food Service">Food Service</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>School</Label>
            <Select 
              value={data.school_id} 
              onValueChange={(value) => updateData('school_id', value)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select school" />
              </SelectTrigger>
              <SelectContent>
                {schools.map((school) => (
                  <SelectItem key={school.id} value={getSchoolKey(school)}>
                    {school.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <Label>Hire Date *</Label>
            <Input
              type="date"
              value={data.hire_date}
              onChange={(e) => updateData('hire_date', e.target.value)}
              required
            />
          </div>
          <div>
            <Label>FTE</Label>
            <Input
              type="number"
              step="0.1"
              min="0"
              max="1"
              value={data.fte}
              onChange={(e) => updateData('fte', parseFloat(e.target.value))}
            />
          </div>
          <div>
            <Label>Annual Salary</Label>
            <Input
              type="number"
              value={data.salary}
              onChange={(e) => updateData('salary', e.target.value)}
              placeholder="Optional"
            />
          </div>
        </div>
        <div>
          <Label>Supervisor</Label>
          <Select 
            value={data.supervisor_id} 
            onValueChange={(value) => updateData('supervisor_id', value)}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select supervisor" />
            </SelectTrigger>
            <SelectContent>
              {supervisors.map((supervisor) => (
                <SelectItem key={supervisor.id} value={supervisor.id}>
                  {supervisor.first_name} {supervisor.last_name} - {supervisor.position_title}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
    </div>
  </div>
);

const ProfessionalInfoStep = ({ data, updateData }) => (
  <div className="space-y-6">
    <div>
      <h3 className="text-lg font-semibold text-gray-900 mb-4">Professional Information</h3>
      <div className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label>State Teacher ID</Label>
            <Input
              value={data.state_teacher_id}
              onChange={(e) => updateData('state_teacher_id', e.target.value)}
            />
          </div>
          <div>
            <Label>License Number</Label>
            <Input
              value={data.license_number}
              onChange={(e) => updateData('license_number', e.target.value)}
            />
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label>License Expiry Date</Label>
            <Input
              type="date"
              value={data.license_expiry}
              onChange={(e) => updateData('license_expiry', e.target.value)}
            />
          </div>
          <div>
            <Label>Years of Experience</Label>
            <Input
              type="number"
              min="0"
              value={data.years_experience}
              onChange={(e) => updateData('years_experience', parseInt(e.target.value) || 0)}
            />
          </div>
        </div>
        <div>
          <Label>Education Level</Label>
          <Select 
            value={data.education_level} 
            onValueChange={(value) => updateData('education_level', value)}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select education level" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="High School">High School</SelectItem>
              <SelectItem value="Associate Degree">Associate Degree</SelectItem>
              <SelectItem value="Bachelor's Degree">Bachelor's Degree</SelectItem>
              <SelectItem value="Master's Degree">Master's Degree</SelectItem>
              <SelectItem value="Doctoral Degree">Doctoral Degree</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
    </div>
  </div>
);

const EmergencyContactsStep = ({ data, updateData, addContact, updateContact, removeContact }) => (
  <div className="space-y-6">
    <div className="flex items-center justify-between">
      <h3 className="text-lg font-semibold text-gray-900">Emergency Contacts</h3>
      <Button onClick={addContact} variant="outline">
        <UserPlus className="w-4 h-4 mr-2" />
        Add Contact
      </Button>
    </div>
    
    <div className="space-y-4">
      {data.emergency_contacts.map((contact, index) => (
        <Card key={index}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-4">
              <h4 className="font-medium">Contact {index + 1}</h4>
              {index > 0 && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => removeContact(index)}
                  className="text-red-600 hover:text-red-700"
                >
                  Remove
                </Button>
              )}
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Name *</Label>
                <Input
                  value={contact.name}
                  onChange={(e) => updateContact(index, 'name', e.target.value)}
                  required
                />
              </div>
              <div>
                <Label>Relationship *</Label>
                <Input
                  value={contact.relationship}
                  onChange={(e) => updateContact(index, 'relationship', e.target.value)}
                  placeholder="e.g., Spouse, Parent, Sibling"
                  required
                />
              </div>
              <div>
                <Label>Primary Phone *</Label>
                <Input
                  value={contact.phone_primary}
                  onChange={(e) => updateContact(index, 'phone_primary', e.target.value)}
                  required
                />
              </div>
              <div>
                <Label>Secondary Phone</Label>
                <Input
                  value={contact.phone_secondary}
                  onChange={(e) => updateContact(index, 'phone_secondary', e.target.value)}
                />
              </div>
              <div className="md:col-span-2">
                <Label>Email</Label>
                <Input
                  type="email"
                  value={contact.email}
                  onChange={(e) => updateContact(index, 'email', e.target.value)}
                />
              </div>
            </div>
            
            <div className="mt-4">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={contact.is_primary}
                  onChange={(e) => updateContact(index, 'is_primary', e.target.checked)}
                  className="mr-2"
                />
                Primary emergency contact
              </label>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  </div>
);

const DocumentsStep = ({ data, updateData }) => (
  <div className="space-y-6">
    <div>
      <h3 className="text-lg font-semibold text-gray-900 mb-4">Required Documents</h3>
      <p className="text-gray-600 mb-4">
        The following documents will need to be collected during the onboarding process:
      </p>
      
      <div className="space-y-3">
        {[
          'Employment Contract',
          'I-9 Form and Supporting Documents',
          'W-4 Tax Form',
          'Direct Deposit Authorization',
          'Emergency Contact Information',
          'Teaching License/Certification (if applicable)',
          'Background Check Authorization',
          'Employee Handbook Acknowledgment'
        ].map((doc, index) => (
          <div key={index} className="flex items-center p-3 bg-gray-50 rounded-lg">
            <FileText className="w-5 h-5 text-gray-600 mr-3" />
            <span className="text-gray-900">{doc}</span>
            <Badge variant="outline" className="ml-auto">Required</Badge>
          </div>
        ))}
      </div>
    </div>
    
    <div>
      <Label>Additional Notes</Label>
      <Textarea
        value={data.notes}
        onChange={(e) => updateData('notes', e.target.value)}
        placeholder="Any additional notes or special instructions for this employee..."
        rows={4}
      />
    </div>
  </div>
);

const ReviewStep = ({ data, schools, supervisors }) => {
  const school = schools.find(s => getSchoolKey(s) === data.school_id);
  const supervisor = supervisors.find(s => s.id === data.supervisor_id);
  
  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Review Information</h3>
        <p className="text-gray-600 mb-6">
          Please review all information before completing the onboarding process.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Basic Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <div><strong>Name:</strong> {data.first_name} {data.middle_name} {data.last_name}</div>
            {data.preferred_name && <div><strong>Preferred:</strong> {data.preferred_name}</div>}
            <div><strong>Email:</strong> {data.email}</div>
            <div><strong>Phone:</strong> {data.phone_primary}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Employment</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <div><strong>Position:</strong> {data.position_title}</div>
            <div><strong>Department:</strong> {data.department}</div>
            <div><strong>School:</strong> {school?.name || 'Not assigned'}</div>
            <div><strong>Hire Date:</strong> {new Date(data.hire_date).toLocaleDateString()}</div>
            <div><strong>FTE:</strong> {data.fte}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Address</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {data.address_line1 && <div>{data.address_line1}</div>}
            {data.address_line2 && <div>{data.address_line2}</div>}
            {(data.city || data.state || data.zip_code) && (
              <div>{data.city}, {data.state} {data.zip_code}</div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Emergency Contacts</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {data.emergency_contacts.filter(c => c.name).map((contact, index) => (
              <div key={index} className="border-b pb-2 last:border-b-0">
                <div><strong>{contact.name}</strong> ({contact.relationship})</div>
                <div className="text-sm text-gray-600">{contact.phone_primary}</div>
                {contact.is_primary && <Badge variant="outline" className="text-xs">Primary</Badge>}
              </div>
            ))}
          </CardContent>
        </Card>
      </div>

      {data.notes && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Notes</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-gray-700">{data.notes}</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default EmployeeOnboarding;